import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, Loader2, Trash2, Copy, Search, Image as ImageIcon, Video, Music,
  FolderOpen, FolderPlus, FolderMinus, Grid3x3, List, X, Check, CheckSquare, Square,
  Edit3, Save, Eye, MoveRight, ChevronDown, Sparkles, Filter, HardDrive, FileText,
  Download, Globe, RefreshCw, Cloud
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_FILTERS = [
  { value: "all", label: "Semua", icon: HardDrive },
  { value: "image", label: "Gambar", icon: ImageIcon },
  { value: "video", label: "Video", icon: Video },
  { value: "audio", label: "Audio", icon: Music },
];

const SORT_OPTIONS = [
  { value: "created_desc", label: "Terbaru" },
  { value: "created_asc", label: "Terlama" },
  { value: "name_asc", label: "Nama A–Z" },
  { value: "name_desc", label: "Nama Z–A" },
  { value: "size_desc", label: "Ukuran terbesar" },
  { value: "size_asc", label: "Ukuran terkecil" },
];

const FIELD_LIGHT = "block w-full bg-white border border-[#D4859B]/20 focus:border-seikat-pink rounded-xl px-3.5 py-2 text-[#1C1A19] placeholder:text-[#A8A29A] font-body text-sm outline-none transition-colors";
const LABEL_LIGHT = "block text-[10px] tracking-[0.2em] uppercase text-[#8A857D] mb-1.5 font-body";

function prettyBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function mediaKind(mime) {
  if (mime?.startsWith("image/")) return "image";
  if (mime?.startsWith("video/")) return "video";
  if (mime?.startsWith("audio/")) return "audio";
  return "other";
}

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("all");
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [view, setView] = useState("grid"); // grid | list
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // {filename: 0-100}
  const [dragOver, setDragOver] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [detailItem, setDetailItem] = useState(null); // currently focused item in side panel
  const [editMeta, setEditMeta] = useState(null); // { name, alt_text, caption, tags, folder } for editing
  const [lightbox, setLightbox] = useState(null); // url currently in lightbox preview
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [bulkMoveFolder, setBulkMoveFolder] = useState("uploads");
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [downloadingLocal, setDownloadingLocal] = useState(false);
  const fileRef = useRef(null);

  const fetchFolders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cms/media/folders`, { withCredentials: true });
      setFolders(data.folders || []);
      setTotalFiles(data.total_files || 0);
    } catch (e) {
      // silent
    }
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (folder && folder !== "all") params.set("folder", folder);
      if (type && type !== "all") params.set("type", type);
      params.set("sort", sort);
      params.set("limit", "500");
      const { data } = await axios.get(`${API}/admin/cms/media?${params.toString()}`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Gagal memuat media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFolders(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const id = setTimeout(fetchMedia, 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q, folder, type, sort]);

  // Stats
  const stats = useMemo(() => {
    const s = { image: 0, video: 0, audio: 0, other: 0, total_size: 0 };
    for (const m of items) {
      s[mediaKind(m.mime)] = (s[mediaKind(m.mime)] || 0) + 1;
      s.total_size += m.size || 0;
    }
    return s;
  }, [items]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let success = 0;
    let failed = 0;
    for (const f of files) {
      setUploadProgress(prev => ({ ...prev, [f.name]: 0 }));
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder !== "all" ? folder : "uploads");
      try {
        await axios.post(`${API}/admin/cms/media`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) {
              const pct = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(prev => ({ ...prev, [f.name]: pct }));
            }
          },
        });
        success++;
      } catch (e) {
        failed++;
        toast.error(`${f.name} gagal upload`);
      }
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[f.name];
        return next;
      });
    }
    setUploading(false);
    if (success) toast.success(`${success} file terupload`);
    if (failed) toast.error(`${failed} file gagal`);
    await fetchFolders();
    await fetchMedia();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(Array.from(e.dataTransfer.files || []));
  };

  const handleCopy = async (url) => {
    const abs = url.startsWith("http") ? url : `${BACKEND}${url}`;
    try {
      await navigator.clipboard.writeText(abs);
      toast.success("URL disalin");
    } catch {
      toast.error("Gagal menyalin URL");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus media ini?")) return;
    try {
      await axios.delete(`${API}/admin/cms/media/${id}`, { withCredentials: true });
      toast.success("Media dihapus");
      if (detailItem?.media_id === id) setDetailItem(null);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      await fetchMedia();
      await fetchFolders();
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAllVisible = () => {
    const next = new Set(selectedIds);
    items.forEach(it => next.add(it.media_id));
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Hapus ${selectedIds.size} media?`)) return;
    try {
      await axios.post(`${API}/admin/cms/media/bulk-delete`, { ids: Array.from(selectedIds) }, { withCredentials: true });
      toast.success(`${selectedIds.size} media dihapus`);
      clearSelection();
      setDetailItem(null);
      await fetchMedia();
      await fetchFolders();
    } catch (e) {
      toast.error("Bulk delete gagal");
    }
  };

  const bulkMove = async () => {
    if (selectedIds.size === 0 || !bulkMoveFolder) return;
    try {
      await axios.post(`${API}/admin/cms/media/bulk-move`, { ids: Array.from(selectedIds), folder: bulkMoveFolder }, { withCredentials: true });
      toast.success(`${selectedIds.size} media dipindah ke ${bulkMoveFolder}`);
      clearSelection();
      setShowBulkMove(false);
      await fetchMedia();
      await fetchFolders();
    } catch (e) {
      toast.error("Pindah folder gagal");
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/admin/cms/media/folders`, { name }, { withCredentials: true });
      toast.success(`Folder "${name}" dibuat`);
      setNewFolderName("");
      setShowNewFolder(false);
      await fetchFolders();
    } catch (e) {
      toast.error("Gagal membuat folder");
    }
  };

  const deleteFolder = async (name) => {
    if (name === "uploads") {
      toast.error("Folder default tidak bisa dihapus");
      return;
    }
    if (!window.confirm(`Hapus folder "${name}"? File di dalamnya akan dipindahkan ke 'uploads'.`)) return;
    try {
      await axios.delete(`${API}/admin/cms/media/folders/${encodeURIComponent(name)}?move_to=uploads`, { withCredentials: true });
      toast.success(`Folder "${name}" dihapus`);
      if (folder === name) setFolder("all");
      await fetchFolders();
      await fetchMedia();
    } catch (e) {
      toast.error("Gagal menghapus folder");
    }
  };

  const openDetail = async (item) => {
    setDetailItem(item);
    setEditMeta(null);
    // fetch usage in background
    try {
      const { data } = await axios.get(`${API}/admin/cms/media/${item.media_id}/usage`, { withCredentials: true });
      setDetailItem(prev => prev?.media_id === item.media_id ? { ...prev, _usage: data } : prev);
    } catch (e) {}
  };

  const startEditMeta = () => {
    if (!detailItem) return;
    setEditMeta({
      name: detailItem.name || "",
      alt_text: detailItem.alt_text || "",
      caption: detailItem.caption || "",
      tags: (detailItem.tags || []).join(", "),
      folder: detailItem.folder || "uploads",
    });
  };

  const saveEditMeta = async () => {
    if (!detailItem || !editMeta) return;
    try {
      const payload = {
        name: editMeta.name.trim() || detailItem.name,
        alt_text: editMeta.alt_text,
        caption: editMeta.caption,
        tags: editMeta.tags.split(",").map(t => t.trim()).filter(Boolean),
        folder: (editMeta.folder || "uploads").trim() || "uploads",
      };
      const { data } = await axios.put(`${API}/admin/cms/media/${detailItem.media_id}`, payload, { withCredentials: true });
      toast.success("Metadata disimpan");
      setDetailItem({ ...data, _usage: detailItem._usage });
      setEditMeta(null);
      await fetchMedia();
      await fetchFolders();
    } catch (e) {
      toast.error("Gagal menyimpan metadata");
    }
  };

  const downloadToLocal = async () => {
    if (!detailItem) return;
    setDownloadingLocal(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/cms/media/${detailItem.media_id}/download-local`,
        {},
        { withCredentials: true, timeout: 180000 }
      );
      if (data.already_local) {
        toast.info("Media sudah tersimpan lokal");
      } else {
        toast.success(`Berhasil unduh ke server (${Math.round((data.downloaded_bytes || 0) / 1024)} KB)`);
      }
      if (data.media) {
        setDetailItem({ ...data.media, _usage: detailItem._usage });
      }
      await fetchMedia();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunduh ke lokal");
    } finally {
      setDownloadingLocal(false);
    }
  };

  const absoluteUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

  return (
    <div data-testid="admin-media" className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-px bg-seikat-pink" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-seikat-pink font-body">Media Library</span>
          </div>
          <h1 className="font-heading text-4xl text-[#1C1A19] leading-tight">Galeri <em className="text-seikat-pink">Media</em></h1>
          <p className="font-body text-sm text-[#4A453F]/75 mt-1.5">Kelola gambar, video, dan audio yang dipakai di seluruh situs.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/admin/cms/media/seed/refresh`, {}, { withCredentials: true });
                if (data.inserted > 0) toast.success(`${data.inserted} sample media baru ditambahkan`);
                else toast.info("Semua sample media sudah ada");
                await fetchFolders();
                await fetchMedia();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Gagal refresh sample");
              }
            }}
            data-testid="media-seed-refresh"
            className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-[#D4859B]/20 hover:border-seikat-pink text-[#4A453F] hover:text-seikat-pink rounded-full text-sm font-body transition-colors"
            title="Re-seed contoh foto & video (idempotent)"
          >
            <RefreshCw size={13} /> Refresh Samples
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="media-upload-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
            Upload Media
          </button>
          <input ref={fileRef} type="file" hidden multiple onChange={e => upload(Array.from(e.target.files || []))} accept="image/*,video/*,audio/*" data-testid="media-file-input" />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total File", value: totalFiles, icon: HardDrive, color: "#1C1A19" },
          { label: "Gambar", value: stats.image, icon: ImageIcon, color: "#D4859B" },
          { label: "Video", value: stats.video, icon: Video, color: "#8A6794" },
          { label: "Audio", value: stats.audio, icon: Music, color: "#A8B85F" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-[#D4859B]/12 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}14` }}>
              <s.icon size={18} style={{ color: s.color }} strokeWidth={1.6} />
            </div>
            <div>
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#8A857D]">{s.label}</p>
              <p className="font-heading text-2xl text-[#1C1A19] leading-none mt-0.5">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main layout: folders sidebar + content */}
      <div className="flex gap-5 min-h-[60vh]">
        {/* Folder sidebar */}
        <aside className="hidden md:flex w-56 flex-shrink-0 flex-col bg-white border border-[#D4859B]/12 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#D4859B]/10 flex items-center justify-between">
            <p className="font-body text-[10px] tracking-[0.25em] uppercase text-[#8A857D]">Folder</p>
            <button
              onClick={() => setShowNewFolder(s => !s)}
              className="w-6 h-6 rounded-full hover:bg-seikat-pink/10 flex items-center justify-center text-seikat-pink"
              title="Tambah folder"
              data-testid="media-add-folder"
            >
              <FolderPlus size={12} />
            </button>
          </div>
          {showNewFolder && (
            <div className="px-3 py-2 border-b border-[#D4859B]/10 bg-[#FDF8F6]">
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); } }}
                  placeholder="Nama folder…"
                  className="flex-1 px-2 py-1 bg-white border border-[#D4859B]/20 rounded-lg text-xs font-body outline-none focus:border-seikat-pink"
                  data-testid="new-folder-name"
                />
                <button onClick={createFolder} className="w-6 h-6 rounded-md bg-seikat-pink text-white hover:bg-seikat-pink/90 flex items-center justify-center" data-testid="new-folder-save">
                  <Check size={11} />
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto py-2 space-y-0.5">
            <button
              onClick={() => setFolder("all")}
              data-testid="folder-all"
              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-[13px] font-body transition-colors ${
                folder === "all" ? "bg-seikat-pink/10 text-seikat-pink font-medium" : "text-[#4A453F] hover:bg-[#FDF8F6]"
              }`}
            >
              <HardDrive size={13} />
              <span className="flex-1">Semua</span>
              <span className="text-[10px] text-[#8A857D]">{totalFiles}</span>
            </button>
            {folders.map(f => (
              <div key={f.name} className="group flex items-center">
                <button
                  onClick={() => setFolder(f.name)}
                  data-testid={`folder-${f.name}`}
                  className={`flex-1 flex items-center gap-2 px-4 py-2 text-left text-[13px] font-body transition-colors ${
                    folder === f.name ? "bg-seikat-pink/10 text-seikat-pink font-medium" : "text-[#4A453F] hover:bg-[#FDF8F6]"
                  }`}
                >
                  <FolderOpen size={13} />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-[10px] text-[#8A857D]">{f.count}</span>
                </button>
                {f.name !== "uploads" && (
                  <button
                    onClick={() => deleteFolder(f.name)}
                    title="Hapus folder"
                    className="opacity-0 group-hover:opacity-100 w-7 h-7 mr-1 rounded-md hover:bg-red-50 text-[#C44] flex items-center justify-center transition-all"
                  >
                    <FolderMinus size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Content area */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Toolbar */}
          <div className="bg-white border border-[#D4859B]/12 rounded-2xl p-3 mb-4 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 bg-[#FDF8F6] border border-[#D4859B]/12 rounded-full px-3.5 py-1.5 flex-1 min-w-[180px]">
              <Search size={13} className="text-[#A8A29A]" />
              <input
                className="bg-transparent outline-none text-sm font-body text-[#1C1A19] placeholder:text-[#A8A29A]/70 flex-1"
                placeholder="Cari nama, alt, tag…"
                value={q}
                onChange={e => setQ(e.target.value)}
                data-testid="media-search"
              />
              {q && <button onClick={() => setQ("")} className="text-[#A8A29A] hover:text-seikat-pink"><X size={12} /></button>}
            </div>
            {/* Type chips */}
            <div className="flex items-center gap-1 bg-[#FDF8F6] border border-[#D4859B]/12 rounded-full p-1">
              {TYPE_FILTERS.map(opt => {
                const active = type === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setType(opt.value)}
                    data-testid={`media-type-${opt.value}`}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-body transition-colors ${
                      active ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:bg-white"
                    }`}
                  >
                    <Icon size={11} /> {opt.label}
                  </button>
                );
              })}
            </div>
            {/* Sort */}
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="px-3 py-1.5 bg-[#FDF8F6] border border-[#D4859B]/12 rounded-full text-xs font-body text-[#4A453F] outline-none focus:border-seikat-pink"
              data-testid="media-sort"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {/* View toggle */}
            <div className="flex items-center gap-1 bg-[#FDF8F6] border border-[#D4859B]/12 rounded-full p-1">
              <button onClick={() => setView("grid")} className={`p-1.5 rounded-full transition-colors ${view === "grid" ? "bg-seikat-pink text-white" : "text-[#4A453F] hover:bg-white"}`} title="Grid" data-testid="view-grid">
                <Grid3x3 size={12} />
              </button>
              <button onClick={() => setView("list")} className={`p-1.5 rounded-full transition-colors ${view === "list" ? "bg-seikat-pink text-white" : "text-[#4A453F] hover:bg-white"}`} title="List" data-testid="view-list">
                <List size={12} />
              </button>
            </div>
          </div>

          {/* Bulk action bar */}
          <AnimatePresence>
            {selectedIds.size > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="bg-seikat-pink text-white rounded-2xl p-3 mb-4 flex items-center justify-between gap-2 flex-wrap"
              >
                <div className="flex items-center gap-2">
                  <CheckSquare size={15} />
                  <span className="font-body text-sm font-medium">{selectedIds.size} dipilih</span>
                  <button onClick={clearSelection} className="text-white/80 hover:text-white text-xs underline ml-2" data-testid="bulk-clear">Bersihkan</button>
                  <button onClick={selectAllVisible} className="text-white/80 hover:text-white text-xs underline" data-testid="bulk-select-all">Pilih semua tampil</button>
                </div>
                <div className="flex items-center gap-2 relative">
                  <button
                    onClick={() => setShowBulkMove(s => !s)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-full text-xs font-body"
                    data-testid="bulk-move"
                  >
                    <MoveRight size={11} /> Pindah Folder
                  </button>
                  {showBulkMove && (
                    <div className="absolute right-0 top-full mt-2 bg-white text-[#1C1A19] rounded-xl shadow-xl border border-[#D4859B]/20 p-3 z-30 min-w-[220px]">
                      <p className="text-[10px] tracking-widest uppercase text-[#8A857D] mb-2">Pindah ke</p>
                      <select
                        value={bulkMoveFolder}
                        onChange={e => setBulkMoveFolder(e.target.value)}
                        className="w-full px-2.5 py-1.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-lg text-sm font-body outline-none mb-2"
                        data-testid="bulk-move-folder-select"
                      >
                        <option value="uploads">uploads (default)</option>
                        {folders.filter(f => f.name !== "uploads").map(f => (
                          <option key={f.name} value={f.name}>{f.name}</option>
                        ))}
                      </select>
                      <button onClick={bulkMove} className="w-full px-3 py-1.5 bg-seikat-pink text-white rounded-lg text-xs font-medium hover:bg-seikat-pink/90" data-testid="bulk-move-confirm">
                        Pindahkan
                      </button>
                    </div>
                  )}
                  <button
                    onClick={bulkDelete}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-full text-xs font-body"
                    data-testid="bulk-delete"
                  >
                    <Trash2 size={11} /> Hapus Semua
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Upload progress overlay */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-3 mb-4 space-y-1.5">
              <p className="font-body text-[10px] tracking-widest uppercase text-[#8A857D] mb-1">Sedang upload</p>
              {Object.entries(uploadProgress).map(([name, pct]) => (
                <div key={name} className="flex items-center gap-2">
                  <Loader2 size={11} className="animate-spin text-seikat-pink" />
                  <span className="font-body text-xs text-[#4A453F] truncate flex-1">{name}</span>
                  <div className="w-32 h-1 bg-[#D4859B]/10 rounded-full overflow-hidden">
                    <div className="h-full bg-seikat-pink transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-body text-[10px] text-[#8A857D] w-8 text-right">{pct}%</span>
                </div>
              ))}
            </div>
          )}

          {/* Dropzone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`rounded-2xl border-2 border-dashed transition-all p-6 text-center mb-4 ${
              dragOver ? "border-seikat-pink bg-seikat-pink/5" : "border-[#D4859B]/20 bg-[#FDF8F6]/50 hover:bg-[#FDF8F6]"
            }`}
            data-testid="media-dropzone"
          >
            <Upload size={20} className="mx-auto text-seikat-pink/60 mb-1.5" strokeWidth={1.5} />
            <p className="font-body text-sm text-[#1C1A19]">Drop file ke sini, atau klik <span className="text-seikat-pink underline cursor-pointer" onClick={() => fileRef.current?.click()}>Upload Media</span></p>
            <p className="font-body text-[11px] text-[#8A857D] mt-1">PNG, JPG, MP4, WebM, MP3 (max 10 MB) → folder <strong>{folder !== "all" ? folder : "uploads"}</strong></p>
          </div>

          {/* Items grid / list */}
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-seikat-pink" size={30} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 bg-white border border-[#D4859B]/12 rounded-2xl">
              <ImageIcon size={36} className="mx-auto mb-3 text-[#D4859B]/30" />
              <p className="font-heading text-xl text-[#1C1A19] mb-1">Belum ada media</p>
              <p className="font-body text-sm text-[#8A857D]">Upload media pertamamu untuk memulai.</p>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="media-grid">
              {items.map(m => {
                const kind = mediaKind(m.mime);
                const isSelected = selectedIds.has(m.media_id);
                const isFocused = detailItem?.media_id === m.media_id;
                return (
                  <div
                    key={m.media_id}
                    data-testid={`media-item-${m.media_id}`}
                    className={`group relative bg-white border rounded-2xl overflow-hidden transition-all ${
                      isFocused ? "border-seikat-pink shadow-[0_0_0_3px_rgba(212,133,155,0.2)]" : "border-[#D4859B]/12 hover:border-seikat-pink/40 hover:shadow-md"
                    }`}
                  >
                    {/* checkbox */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(m.media_id); }}
                      className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                        isSelected ? "bg-seikat-pink border-seikat-pink text-white" : "bg-white/80 border-[#D4859B]/30 backdrop-blur-sm opacity-0 group-hover:opacity-100"
                      }`}
                      data-testid={`select-${m.media_id}`}
                    >
                      {isSelected && <Check size={12} />}
                    </button>
                    <button
                      onClick={() => openDetail(m)}
                      onDoubleClick={() => kind === "image" && setLightbox(absoluteUrl(m.url))}
                      className="block w-full aspect-square bg-[#FDF8F6]"
                    >
                      {kind === "image" ? (
                        <img src={absoluteUrl(m.url)} alt={m.alt_text || m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : kind === "video" ? (
                        <div className="w-full h-full bg-[#1C1A19]/5 flex flex-col items-center justify-center gap-1">
                          <Video size={26} className="text-seikat-pink/70" />
                          <span className="text-[10px] font-body text-[#4A453F] px-2 truncate max-w-full">{m.name}</span>
                        </div>
                      ) : kind === "audio" ? (
                        <div className="w-full h-full bg-[#C5DB7B]/10 flex flex-col items-center justify-center gap-1">
                          <Music size={26} className="text-[#A8B85F]" />
                          <span className="text-[10px] font-body text-[#4A453F] px-2 truncate max-w-full">{m.name}</span>
                        </div>
                      ) : (
                        <div className="w-full h-full bg-[#1C1A19]/5 flex items-center justify-center">
                          <FileText size={26} className="text-[#8A857D]" />
                        </div>
                      )}
                    </button>
                    {Array.isArray(m.tags) && m.tags.length > 0 && (
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-[9px] font-body">{m.tags[0]}{m.tags.length > 1 ? ` +${m.tags.length - 1}` : ""}</div>
                    )}
                    {m.is_seed && (
                      <div className="absolute bottom-[60px] left-2 px-1.5 py-0.5 rounded-full bg-[#C5DB7B] text-[#1C1A19] text-[9px] font-body font-medium flex items-center gap-1 shadow-sm">
                        <Sparkles size={8} /> SAMPLE
                      </div>
                    )}
                    {m.url?.startsWith("http") && !m.is_local && (
                      <div className="absolute top-2 right-2 mt-6 w-5 h-5 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-seikat-pink shadow-sm" title="External CDN URL">
                        <Cloud size={10} />
                      </div>
                    )}
                    <div className="p-2.5 border-t border-[#D4859B]/8">
                      <p className="font-body text-[11px] text-[#1C1A19] truncate" title={m.name}>{m.name}</p>
                      <p className="font-body text-[10px] text-[#8A857D]">{prettyBytes(m.size)} · {m.folder}</p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <button onClick={(e) => { e.stopPropagation(); handleCopy(m.url); }} className="flex-1 text-[10px] py-1 rounded-md bg-[#FDF8F6] hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] transition-colors" data-testid={`copy-${m.media_id}`}>
                          <Copy size={10} className="inline mr-1" /> Copy
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); openDetail(m); }} className="text-[10px] py-1 px-2 rounded-md bg-[#FDF8F6] hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] transition-colors" data-testid={`info-${m.media_id}`}>
                          <Edit3 size={10} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(m.media_id); }} className="text-[10px] py-1 px-2 rounded-md bg-[#FDF8F6] hover:bg-red-50 hover:text-red-500 text-[#4A453F] transition-colors" data-testid={`del-${m.media_id}`}>
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // List view
            <div className="bg-white border border-[#D4859B]/12 rounded-2xl overflow-hidden" data-testid="media-list">
              <table className="w-full">
                <thead className="bg-[#FDF8F6] border-b border-[#D4859B]/10">
                  <tr className="text-[10px] tracking-widest uppercase text-[#8A857D] font-body">
                    <th className="px-3 py-2.5 text-left w-10"></th>
                    <th className="px-3 py-2.5 text-left">Preview</th>
                    <th className="px-3 py-2.5 text-left">Nama</th>
                    <th className="px-3 py-2.5 text-left hidden md:table-cell">Folder</th>
                    <th className="px-3 py-2.5 text-left hidden md:table-cell">Ukuran</th>
                    <th className="px-3 py-2.5 text-left hidden lg:table-cell">Tag</th>
                    <th className="px-3 py-2.5 text-right w-32">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(m => {
                    const kind = mediaKind(m.mime);
                    const isSelected = selectedIds.has(m.media_id);
                    return (
                      <tr key={m.media_id} className={`border-b border-[#D4859B]/8 last:border-b-0 hover:bg-[#FDF8F6]/40 ${isSelected ? "bg-seikat-pink/5" : ""}`}>
                        <td className="px-3 py-2">
                          <button onClick={() => toggleSelect(m.media_id)} className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? "bg-seikat-pink border-seikat-pink text-white" : "border-[#D4859B]/30 text-transparent hover:border-seikat-pink"}`}>
                            <Check size={10} />
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <div className="w-12 h-12 rounded-lg bg-[#FDF8F6] overflow-hidden flex items-center justify-center">
                            {kind === "image" ? (
                              <img src={absoluteUrl(m.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : kind === "video" ? (
                              <Video size={16} className="text-seikat-pink/70" />
                            ) : kind === "audio" ? (
                              <Music size={16} className="text-[#A8B85F]" />
                            ) : (
                              <FileText size={16} className="text-[#8A857D]" />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 font-body text-sm text-[#1C1A19]">
                          <button onClick={() => openDetail(m)} className="hover:text-seikat-pink truncate text-left max-w-xs block">{m.name}</button>
                          <p className="text-[10px] text-[#8A857D] font-body">{m.mime}</p>
                        </td>
                        <td className="px-3 py-2 font-body text-xs text-[#4A453F] hidden md:table-cell">{m.folder}</td>
                        <td className="px-3 py-2 font-body text-xs text-[#4A453F] hidden md:table-cell">{prettyBytes(m.size)}</td>
                        <td className="px-3 py-2 hidden lg:table-cell">
                          {Array.isArray(m.tags) && m.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {m.tags.slice(0, 3).map(t => <span key={t} className="px-1.5 py-0.5 rounded-full bg-seikat-pink/10 text-seikat-pink text-[10px] font-body">{t}</span>)}
                            </div>
                          ) : (
                            <span className="text-[10px] text-[#A8A29A]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => handleCopy(m.url)} className="p-1.5 rounded-md hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] transition-colors" title="Copy URL"><Copy size={12} /></button>
                            <button onClick={() => openDetail(m)} className="p-1.5 rounded-md hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] transition-colors" title="Detail"><Edit3 size={12} /></button>
                            <button onClick={() => handleDelete(m.media_id)} className="p-1.5 rounded-md hover:bg-red-50 hover:text-red-500 text-[#4A453F] transition-colors" title="Hapus"><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail side panel */}
        <AnimatePresence>
          {detailItem && (
            <motion.aside
              initial={{ x: 320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 320, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="hidden xl:flex w-80 flex-shrink-0 flex-col bg-white border border-[#D4859B]/12 rounded-2xl overflow-hidden self-start sticky top-4"
              data-testid="media-detail-panel"
            >
              <div className="px-4 py-3 border-b border-[#D4859B]/10 flex items-center justify-between">
                <p className="font-body text-[10px] tracking-[0.25em] uppercase text-[#8A857D]">Detail</p>
                <button onClick={() => setDetailItem(null)} className="w-7 h-7 rounded-full hover:bg-seikat-pink/10 text-[#4A453F] flex items-center justify-center">
                  <X size={13} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* preview */}
                <div className="rounded-xl overflow-hidden bg-[#FDF8F6] aspect-square flex items-center justify-center">
                  {mediaKind(detailItem.mime) === "image" ? (
                    <img src={absoluteUrl(detailItem.url)} alt={detailItem.alt_text || detailItem.name} className="w-full h-full object-cover cursor-zoom-in" onClick={() => setLightbox(absoluteUrl(detailItem.url))} />
                  ) : mediaKind(detailItem.mime) === "video" ? (
                    <video src={absoluteUrl(detailItem.url)} controls className="w-full h-full object-cover" />
                  ) : mediaKind(detailItem.mime) === "audio" ? (
                    <div className="w-full p-4 flex flex-col items-center justify-center gap-2">
                      <Music size={32} className="text-[#A8B85F]" />
                      <audio src={absoluteUrl(detailItem.url)} controls className="w-full max-w-full" />
                    </div>
                  ) : (
                    <FileText size={32} className="text-[#8A857D]" />
                  )}
                </div>

                {editMeta ? (
                  // Edit mode
                  <>
                    <div><label className={LABEL_LIGHT}>Nama</label><input className={FIELD_LIGHT} value={editMeta.name} onChange={e => setEditMeta(m => ({ ...m, name: e.target.value }))} data-testid="edit-name" /></div>
                    <div><label className={LABEL_LIGHT}>Alt Text (SEO/Accessibility)</label><input className={FIELD_LIGHT} value={editMeta.alt_text} onChange={e => setEditMeta(m => ({ ...m, alt_text: e.target.value }))} data-testid="edit-alt" /></div>
                    <div><label className={LABEL_LIGHT}>Caption</label><textarea rows={2} className={FIELD_LIGHT} value={editMeta.caption} onChange={e => setEditMeta(m => ({ ...m, caption: e.target.value }))} data-testid="edit-caption" /></div>
                    <div><label className={LABEL_LIGHT}>Tag (pisah dengan koma)</label><input className={FIELD_LIGHT} value={editMeta.tags} onChange={e => setEditMeta(m => ({ ...m, tags: e.target.value }))} placeholder="hero, dark, mood" data-testid="edit-tags" /></div>
                    <div>
                      <label className={LABEL_LIGHT}>Folder</label>
                      <select className={FIELD_LIGHT} value={editMeta.folder} onChange={e => setEditMeta(m => ({ ...m, folder: e.target.value }))} data-testid="edit-folder">
                        <option value="uploads">uploads</option>
                        {folders.filter(f => f.name !== "uploads").map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button onClick={saveEditMeta} className="flex-1 px-3 py-2 bg-seikat-pink text-white rounded-full text-xs font-body font-medium hover:bg-seikat-pink/90 flex items-center justify-center gap-1.5" data-testid="edit-save"><Save size={11} /> Simpan</button>
                      <button onClick={() => setEditMeta(null)} className="px-3 py-2 border border-[#D4859B]/20 rounded-full text-xs font-body text-[#4A453F] hover:bg-[#FDF8F6]">Batal</button>
                    </div>
                  </>
                ) : (
                  // Read mode
                  <>
                    <div>
                      <p className="font-heading text-lg text-[#1C1A19] leading-snug break-words" data-testid="detail-name">{detailItem.name}</p>
                      <p className="font-body text-[10px] text-[#8A857D] mt-1">{detailItem.mime} · {prettyBytes(detailItem.size)}</p>
                    </div>
                    {detailItem.alt_text && (
                      <div>
                        <p className={LABEL_LIGHT}>Alt Text</p>
                        <p className="font-body text-sm text-[#1C1A19]">{detailItem.alt_text}</p>
                      </div>
                    )}
                    {detailItem.caption && (
                      <div>
                        <p className={LABEL_LIGHT}>Caption</p>
                        <p className="font-body text-sm text-[#1C1A19] italic">{detailItem.caption}</p>
                      </div>
                    )}
                    <div>
                      <p className={LABEL_LIGHT}>Folder</p>
                      <p className="font-body text-sm text-[#1C1A19]"><FolderOpen size={11} className="inline mr-1 text-seikat-pink" />{detailItem.folder}</p>
                    </div>
                    {Array.isArray(detailItem.tags) && detailItem.tags.length > 0 && (
                      <div>
                        <p className={LABEL_LIGHT}>Tag</p>
                        <div className="flex flex-wrap gap-1">
                          {detailItem.tags.map(t => <span key={t} className="px-2 py-0.5 rounded-full bg-seikat-pink/10 text-seikat-pink text-[10px] font-body">{t}</span>)}
                        </div>
                      </div>
                    )}
                    {detailItem._usage && (
                      <div>
                        <p className={LABEL_LIGHT}>Dipakai oleh ({detailItem._usage.count || 0})</p>
                        {(detailItem._usage.usages || []).length === 0 ? (
                          <p className="font-body text-xs text-[#A8A29A] italic">Belum dipakai di mana pun</p>
                        ) : (
                          <ul className="space-y-1">
                            {(detailItem._usage.usages || []).slice(0, 8).map((u, i) => (
                              <li key={i} className="flex items-center gap-1.5 font-body text-xs text-[#4A453F]">
                                <Sparkles size={10} className="text-seikat-pink flex-shrink-0" />
                                <span className="truncate">{u.label || u.slug} <em className="text-[#A8A29A]">({u.type})</em></span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <div>
                      <p className={LABEL_LIGHT}>URL</p>
                      <div className="flex items-center gap-1">
                        <input readOnly value={absoluteUrl(detailItem.url)} className="flex-1 px-2 py-1.5 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-lg text-[10px] font-mono text-[#4A453F] outline-none" />
                        <button onClick={() => handleCopy(detailItem.url)} className="p-1.5 rounded-lg hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] transition-colors" title="Copy URL" data-testid="detail-copy">
                          <Copy size={11} />
                        </button>
                      </div>
                      {detailItem.url?.startsWith("http") ? (
                        <p className="flex items-center gap-1 mt-1.5 text-[10px] text-seikat-pink font-body">
                          <Cloud size={9} /> Eksternal CDN — bergantung pada koneksi outbound
                        </p>
                      ) : (
                        <p className="flex items-center gap-1 mt-1.5 text-[10px] text-[#A8B85F] font-body">
                          <HardDrive size={9} /> Tersimpan lokal di server
                        </p>
                      )}
                    </div>
                    {(detailItem.is_seed || detailItem.source || detailItem.license) && (
                      <div className="rounded-xl bg-[#C5DB7B]/8 border border-[#C5DB7B]/30 p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Sparkles size={11} className="text-[#A8B85F]" />
                          <p className="text-[10px] tracking-widest uppercase text-[#A8B85F] font-medium">Sample Media</p>
                        </div>
                        {detailItem.source && <p className="font-body text-[11px] text-[#1C1A19]"><strong>Source:</strong> {detailItem.source}</p>}
                        {detailItem.license && <p className="font-body text-[10px] text-[#4A453F] mt-0.5 leading-snug">{detailItem.license}</p>}
                        {detailItem.is_local && (
                          <p className="font-body text-[10px] text-[#A8B85F] mt-1.5 flex items-center gap-1"><Check size={9} /> Sudah diunduh lokal — portable</p>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <button onClick={startEditMeta} className="flex-1 px-3 py-2 bg-seikat-pink text-white rounded-full text-xs font-body font-medium hover:bg-seikat-pink/90 flex items-center justify-center gap-1.5" data-testid="detail-edit">
                        <Edit3 size={11} /> Edit Metadata
                      </button>
                      <button onClick={() => handleDelete(detailItem.media_id)} className="px-3 py-2 border border-red-200 rounded-full text-xs font-body text-red-500 hover:bg-red-50" data-testid="detail-delete">
                        <Trash2 size={11} />
                      </button>
                    </div>
                    {detailItem.url?.startsWith("http") && !detailItem.is_local && (
                      <button
                        onClick={downloadToLocal}
                        disabled={downloadingLocal}
                        data-testid="detail-download-local"
                        className="w-full px-3 py-2 border border-[#A8B85F]/40 text-[#7a8a44] hover:bg-[#C5DB7B]/15 rounded-full text-xs font-body font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                        title="Unduh file dari CDN ke server agar 100% portable"
                      >
                        {downloadingLocal ? <Loader2 className="animate-spin" size={11} /> : <Download size={11} />}
                        {downloadingLocal ? "Mengunduh\u2026" : "Unduh ke Server (Portable)"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] bg-[#1C1A19]/90 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="media-lightbox"
          >
            <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
              <X size={18} />
            </button>
            <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
